import { usePageData } from "@/lib/pageData";
import { SITE_URL } from "@/lib/business";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { ServiceArea } from "@/components/ServiceArea";
import { serviceListSchema } from "@/lib/structuredData";

export default function CategoryPage() {
  const { slug } = useParams();
  const initial = usePageData();
  const [storedCategory, setCategory] = useState(initial?.category || null);
  const category = storedCategory?.slug === slug ? storedCategory : initial?.category || null;
  const [storedServices, setServices] = useState(initial?.categoryServices || []);
  const services = storedServices.filter((service) => service.category_slug === slug);
  const [missingSlug, setMissingSlug] = useState(null);

  useEffect(() => {
    let active = true;
    setMissingSlug(null);
    setCategory(initial?.category || null);
    setServices(initial?.categoryServices || []);
    Promise.all([api.get('/categories'), api.get('/services?category=' + slug)])
      .then(([c, s]) => {
        if (!active) return;
        const found = c.data.find((x) => x.slug === slug);
        if (!found) { setMissingSlug(slug); return; }
        setCategory(found);
        setServices(s.data);
      })
      .catch(() => { if (active && !initial?.category) setMissingSlug(slug); });
    return () => { active = false; };
  }, [slug, initial]);

  if (missingSlug === slug) {
    return (
      <div className="container-x py-24 text-center" data-testid="category-not-found">
        <SEO title="Category Not Found | Mynavodayans" description="This service category is unavailable." noindex />
        <h1 className="font-heading text-2xl font-bold text-[#0a192f]">Category not found</h1>
        <Link to="/services" className="btn-navy mt-6">Browse all services</Link>
      </div>
    );
  }
  if (!category) return <><SEO title="Service Category | Mynavodayans" description="Loading service category." noindex /><p className="container-x py-24 text-center text-slate-500">Loading…</p></>;

  const jsonLd = [{
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Services", item: SITE_URL + "/services" },
      { "@type": "ListItem", position: 3, name: category.name, item: SITE_URL + `/services/category/${slug}` },
    ],
  }, serviceListSchema(services, `/services/category/${slug}`, category.name)];

  return (
    <div className="container-x py-14" data-testid="category-page">
      <SEO title={`${category.name} in India | Mynavodayans`}
           description={`${category.short} For businesses across India, coordinated from Satna. Confirm availability and requirements for your location.`}
           canonicalPath={`/services/category/${slug}`} jsonLd={jsonLd} pageType="CollectionPage" />
      <nav aria-label="Breadcrumb" className="text-xs text-slate-500" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#b07f0a]">Home</Link> <span aria-hidden="true">/</span>{" "}
        <Link to="/services" className="hover:text-[#b07f0a]">Services</Link> <span aria-hidden="true">/</span>{" "}
        <span className="font-semibold text-slate-700">{category.name}</span>
      </nav>
      <h1 className="mt-3 font-heading text-3xl font-extrabold text-[#0a192f] sm:text-4xl">{category.name}</h1>
      <p className="mt-3 max-w-2xl text-slate-600">{category.short} We assist businesses across India from Satna. Share your city and state to confirm availability and applicable requirements.</p>

      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-services-grid">
        {services.map((s) => (
          <article key={s.slug} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#d4a017]/60 hover:shadow-lg" data-testid={`service-card-${s.slug}`}>
            <h2 className="font-heading text-lg font-bold leading-snug text-[#0a192f]">
              <Link to={`/services/${s.slug}`} className="hover:text-[#b07f0a]">{s.name}</Link>
            </h2>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.short_description}</p>
            <Link to={`/contact?service=${s.slug}`} className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#0a192f] px-4 py-2 text-xs font-bold text-white hover:bg-[#11294a]" data-testid={`service-enquire-${s.slug}`}>
              Enquire <ArrowRight size={13} />
            </Link>
          </article>
        ))}
      </div>
      <ServiceArea />
    </div>
  );
}
